import { type HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { PositionSkillsHttpClient } from './position-skills.http-client';
import { PositionSkillsActions } from './position-skills.store';

@Injectable({ providedIn: 'root' })
export class PositionSkillsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(PositionSkillsHttpClient);

  public fetchPositionSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.fetchPositionSkills),
      switchMap(({ payload }) =>
        this.http.fetchPositionSkills(payload.positionGuid).pipe(
          map(data => PositionSkillsActions.fetchPositionSkillsSuccess({ payload: { data } })),
          catchError((error: HttpErrorResponse) =>
            of(PositionSkillsActions.fetchPositionSkillsFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public assignSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.assignSkill),
      switchMap(({ payload }) =>
        this.http.assignSkill(payload.positionGuid, payload.skillId, payload.proficiencyId).pipe(
          map(() =>
            PositionSkillsActions.fetchPositionSkills({
              payload: { positionGuid: payload.positionGuid },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(PositionSkillsActions.assignSkillFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public updateSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.updateSkill),
      switchMap(({ payload }) =>
        this.http.updateSkill(payload.positionGuid, payload.skillId, payload.proficiencyId).pipe(
          map(() =>
            PositionSkillsActions.updateSkillSuccess({
              payload: { skillId: payload.skillId, proficiencyId: payload.proficiencyId },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(PositionSkillsActions.updateSkillFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public unassignSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.unassignSkill),
      switchMap(({ payload }) =>
        this.http.unassignSkill(payload.positionGuid, payload.skillId).pipe(
          map(() =>
            PositionSkillsActions.unassignSkillSuccess({
              payload: { skillId: payload.skillId },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(PositionSkillsActions.unassignSkillFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public unassignSkillMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.unassignSkillMapping),
      switchMap(({ payload }) =>
        this.http
          .unassignSkillMapping(payload.positionGuid, payload.skillMappingId, payload.levelId)
          .pipe(
            map(() =>
              PositionSkillsActions.unassignSkillMappingSuccess({
                payload: { skillMappingId: payload.skillMappingId },
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(PositionSkillsActions.unassignSkillMappingFailed({ payload: { error } })),
            ),
          ),
      ),
    ),
  );

  public assignSkillMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PositionSkillsActions.assignSkillMapping),
      switchMap(({ payload }) =>
        this.http
          .assignSkillMapping(payload.positionGuid, payload.skillMappingId, payload.levelId)
          .pipe(
            map(() =>
              PositionSkillsActions.fetchPositionSkills({
                payload: { positionGuid: payload.positionGuid },
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(PositionSkillsActions.assignSkillMappingFailed({ payload: { error } })),
            ),
          ),
      ),
    ),
  );
}
